import { useEffect } from 'react'
import { SmartPayTracking } from './smart-pay-tracking.store'

interface IUseSmartPayTrackingParams {
  /** The current pay input value (used to seed the initial rate on mount). */
  payValue: string
  /** The value the Smart Pay feature currently recommends. */
  recommendedPay: number
  /** The form's pay-rate change handler, wrapped to record a manual change. */
  onPayRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * Wires the Smart Pay component's lifecycle into the analytics store:
 * seeds the store when the field is first shown and keeps the recommended rate
 * current. Flow owners clear the store on submit/close so multi-step unmounts
 * do not drop analytics before the shift is saved.
 */
export const useSmartPayTracking = ({
  payValue,
  recommendedPay,
  onPayRateChange,
}: IUseSmartPayTrackingParams) => {
  // Seed Smart Pay analytics state once the field is shown, and clear it on unmount.
  useEffect(() => {
    SmartPayTracking.init({
      initialPayRate: Number(payValue),
      smartPayRate: recommendedPay,
    })
    // Capture the initial values only; subsequent updates are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep the recommended rate current as the selected strategy changes.
  useEffect(() => {
    SmartPayTracking.setSmartPayRate(recommendedPay)
  }, [recommendedPay])

  const handleManualPayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    SmartPayTracking.markManualChange()
    onPayRateChange(e)
  }

  const markSmartPayClick = () => {
    SmartPayTracking.markSmartPayClick()
  }

  return { handleManualPayChange, markSmartPayClick }
}
